import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { CERTIFICATE_REQUESTS_LIST } from '../../graphql/insurancePolicies';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Box, 
  Typography, 
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  Description as DescriptionIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

const CertificateRequestList = ({ statusFilter, onEdit }) => {
  const { data, loading, error } = useQuery(CERTIFICATE_REQUESTS_LIST);

  if (loading) return <CircularProgress />;
  if (error) return <div>Error loading certificate requests: {error.message}</div>;

  const requests = data?.certificateRequestsList?.items || [];
  
  // Filter requests by status if statusFilter is provided
  const filteredRequests = statusFilter 
    ? requests.filter(request => request.status === statusFilter)
    : requests;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getEmailStatusIcon = (request) => {
    if (request.emailSentDate) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    return <WarningIcon color="warning" fontSize="small" />;
  };

  const getEmailStatusTooltip = (request) => {
    if (request.emailSentDate) {
      return `Email sent on ${dayjs(request.emailSentDate).format('MMM DD, YYYY')}`;
    }
    return 'Email not sent yet';
  };

  const getCertificateStatusIcon = (request) => {
    if (request.certificateReceivedDate) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    return <WarningIcon color="warning" fontSize="small" />;
  };

  const getCertificateStatusTooltip = (request) => {
    if (request.certificateReceivedDate) {
      return `Certificate received on ${dayjs(request.certificateReceivedDate).format('MMM DD, YYYY')}`;
    }
    return 'Certificate not received yet';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Certificate Requests</Typography>
        <Button 
          variant="contained" 
          startIcon={<DescriptionIcon />}
          onClick={() => onEdit?.(null)} // Pass null to indicate new request
        >
          New Certificate Request
        </Button>
      </Box>

      {filteredRequests.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No certificate requests found{statusFilter ? ` with status "${statusFilter}"` : ''}.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Insurance Policy</TableCell>
              <TableCell>Business Unit</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email Status</TableCell>
              <TableCell>Certificate Status</TableCell>
              <TableCell>Certificate Number</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {request.client?.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request.client?.address1}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {request.insurancePolicy?.policyNumber}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request.insurancePolicy?.provider}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {request.insurancePolicy?.businessUnit?.name || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {dayjs(request.requestDate).format('MMM DD, YYYY')}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={request.status} 
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={getEmailStatusTooltip(request)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getEmailStatusIcon(request)}
                      <Typography variant="caption">
                        {request.emailSentDate ? 'Sent' : 'Pending'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={getCertificateStatusTooltip(request)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getCertificateStatusIcon(request)}
                      <Typography variant="caption">
                        {request.certificateReceivedDate ? 'Received' : 'Pending'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {request.certificateNumber || '-'}
                  </Typography>
                  {request.effectiveDates && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {request.effectiveDates}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Request">
                      <IconButton 
                        size="small" 
                        onClick={() => onEdit?.(request)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredRequests.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Summary:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${filteredRequests.length}`} 
              size="small" 
              variant="outlined"
            />
            <Chip 
              label={`Pending: ${filteredRequests.filter(r => r.status === 'pending').length}`} 
              size="small" 
              color="warning"
            />
            <Chip 
              label={`Completed: ${filteredRequests.filter(r => r.status === 'completed').length}`} 
              size="small" 
              color="success"
            />
            <Chip 
              label={`Cancelled: ${filteredRequests.filter(r => r.status === 'cancelled').length}`} 
              size="small" 
              color="error"
            />
            <Chip 
              label={`Emails Sent: ${filteredRequests.filter(r => r.emailSentDate).length}`} 
              size="small" 
              color="info"
            />
            <Chip 
              label={`Certificates Received: ${filteredRequests.filter(r => r.certificateReceivedDate).length}`} 
              size="small" 
              color="success"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CertificateRequestList;
